import {ConfigProvider} from 'antd'
import {
    doc_changeCodiDocumentRowType_Api,
    docs_addCodiDocumentRow_Api,
    docs_deleteCodiDocumentRow_Api,
    docs_editCodiDocumentRow_Api,
    docs_reArrangeCodiDocumentRow_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import {UseHook} from 'components/UseHook'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useState} from 'react'
import {Switch} from 'react-router-dom'
import RouteWithSubRoutes from 'routes/RouteWithSubRoutes'
import {NullAsDefault, renderElse, renderIf} from 'views/Shared'
import {CodiActions} from './codiHelpers'
import {showDeleteConfirmDialog} from '../factory/createConfirmDialog'
import {notifyOnError} from '../factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from '../factory/createNotification'
import {bindQueryParams} from '../functions/routerHelper'
import CodiContext from './CodiContext'
import CodiRowRender from './CodiRowRender'
import {settingRoutes} from './routes'
import {useRouteSearchParam} from "../../../envs/_current/history";


const RowSettingContext = React.createContext({})

export const useCodiRowSettings = ({
                                       row_id,
                                       codi_id,
                                       notifyListeners = {
                                           onCreated: undefined,
                                           onUpdated: undefined,
                                           onDeleted: undefined,
                                           onRearranged: undefined,
                                           onTypeChanged: undefined,
                                       },
                                   }) => {
    const t = useTranslate()

    const {
        onCreated = NullAsDefault,
        onUpdated = NullAsDefault,
        onDeleted = NullAsDefault,
        onRearranged = NullAsDefault,
        onTypeChanged = NullAsDefault,
    } = notifyListeners ?? {}

    const onError = notifyOnError(t)

    const {handleAsyncAction: onCreate, ...asyncCreating} = useAsyncAction({
        onError,
        apiInfo: docs_addCodiDocumentRow_Api,
        query: bindQueryParams([{id: codi_id}, {row_id}]),
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.CREATE, t)
            onCreated(result, data)
        },
    })

    const {handleAsyncAction: onUpdate, ...asyncUpdating} = useAsyncAction({
        onError,
        apiInfo: docs_editCodiDocumentRow_Api,
        query: bindQueryParams([{id: codi_id}, {row_id}]),
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onUpdated(result, data)
        },
    })

    const {handleAsyncAction: onDelete, ...asyncDeleting} = useAsyncAction({
        onError,
        apiInfo: docs_deleteCodiDocumentRow_Api,
        query: bindQueryParams([{id: codi_id}, {row_id}]),
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.DELETE, t)
            onDeleted(result, data)
        },
    })

    const {handleAsyncAction: changeRowType, asyncTypeChanging} =
        useAsyncAction({
            apiInfo: doc_changeCodiDocumentRowType_Api,
            query: bindQueryParams([{id: codi_id}, {row_id}]),
            onSuccess: (result, data) => {
                successNotify(NotificationActionTypes.UPDATE, t)
                onTypeChanged(result, data)
            },
            onError: notifyOnError(t),
        })

    const {handleAsyncAction: rearrange, ...asyncRearranging} = useAsyncAction({
        apiInfo: docs_reArrangeCodiDocumentRow_Api,
        query: bindQueryParams([{id: codi_id}, {row_id}]),
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onRearranged(result, data)
        },
        onError: notifyOnError(t),
    })

    return {
        onCreate,
        onUpdate,
        onDelete,
        rearrange,
        changeRowType,
        asyncCreating,
        asyncUpdating,
        asyncDeleting,
        asyncRearranging,
        asyncTypeChanging,
    }
}

const SettingProvider = ({
                             codi,
                             item,
                             children,
                             initialCurrent,
                             ...props
                         }) => {
    const t = useTranslate()

    const [current, setCurrent] = useState(initialCurrent)

    const [refreshKey, setRefreshKey] = useState()

    const onRefresh = () => {
        setRefreshKey(Date.now())
    }

    const row_id = item?.id

    const codi_id = getId(codi)

    // const {
    //   response,
    //   handleAsyncAction = NullAsDefault,
    //   ...rest
    // } = useAsyncAction({
    //   onError: notifyOnError(t),
    //   apiInfo: docs_getCodiDocumentsRowDetails_Api,
    // })

    // useEffect(() => {
    //   if (row_id) {
    //     handleAsyncAction({}, { id: row_id })
    //   }
    // }, [row_id, refreshKey])

    // const item = getResponseItem(response)

    const {
        onCreate = NullAsDefault,
        onUpdate = NullAsDefault,
        onDelete = NullAsDefault,
        rearrange = NullAsDefault,
        changeRowType = NullAsDefault,
    } = useCodiRowSettings({
        row_id,
        codi_id,
        notifyListeners: {
            onCreated: onRefresh,
            onUpdated: onRefresh,
            onDeleted: onRefresh,
            onRearranged: onRefresh,
            onTypeChanged: onRefresh,
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case CodiActions.INSERT_CONTENT:
                onCreate(params)
                break
            case CodiActions.EDIT_CONTENT:
                onUpdate(params)
                break
            case CodiActions.DELETE_CONTENT:
                showDeleteConfirmDialog({
                    translate: t,
                    title: 'delete',
                    message: 'are you sure',
                    onOk: () => {
                        onDelete()
                    },
                })
                break
            case CodiActions.REARRANGE_CONTENT:
                rearrange(params)
                break
            case CodiActions.UPDATE_CODI_ROW_TYPE:
                changeRowType(params)
                break
            default:
                break
        }
    }

    return (
        <RowSettingContext.Provider
            value={{
                item,
                codi,
                row_id,
                codi_id,
                current,
                onRefresh,
                setCurrent,
                refreshKey,
                handleAction,
                // ...rest,
                ...props,
            }}>
            <React.Fragment>
                {children}
                {current && (
                    <Switch
                        location={{
                            hash: '',
                            search: '',
                            key: current,
                            pathname: current,
                        }}>
                        {Object.values(settingRoutes).map((route, index) => (
                            <RouteWithSubRoutes
                                key={index}
                                {...route}
                            />
                        ))}
                    </Switch>
                )}
            </React.Fragment>
        </RowSettingContext.Provider>
    )
}

export const RowDescription = ({item, style, className}) => {
    const t = useTranslate()
    const {content, is_html, short_index_name} = item ?? {}
    return (
        <div
            style={style}
            className={className}>
            {renderIf(
                short_index_name,
                <span className="text-color-000 font-bold">{`${short_index_name}: `}</span>
            )}
            {Boolean(is_html) ? (
                <div className="flex flex-col flex-center gap-1 py-3 background-100 rounded-lg mt-2">
                    <CodiRowRender.HTMLRenderer html={content}/>
                </div>
            ) : (
                <span className="text-color-100 font-medium">{content}</span>
            )}
        </div>
    )
}

export const SettingRowHeader = () => {
    const t = useTranslate()
    const {item, codi} = useContext(RowSettingContext)
    return renderIf(
        item,
        <React.Fragment>
            {renderElse(
                _.isEmpty(codi),
                <div className="flex items-baseline gap-2 border-b mb-2">
          <span className="capitalize italic text-sm text-color-400">
            {`${t('document')}: `}
          </span>
                    <span className="flex-1 font-bold text-doc-lg text-primary">
            {getTitle(codi)}
          </span>
                </div>
            )}
            <RowDescription
                item={item}
                className="text-sm space-x-2"
            />
            <div className="flex items-center gap-2 text-sm italic mt-1 mb-6">
        <span className="font-light text-color-400">{`${t(
            'current index typecd'
        )}: `}</span>
                <span className="font-bold text-color-000">
          {t(item?.index_typecd)}
        </span>
            </div>
        </React.Fragment>
    )
}

export const RowSettingProvider = (props) => (
    <UseHook
        hook={useRouteSearchParam}
        deps={['initialCurrent']}>
        {(initialCurrent) => (
            <ConfigProvider renderEmpty={EmptyHolder}>
                <CodiContext.Consumer>
                    {({item, system_row}) => (
                        <SettingProvider
                            {...props}
                            codi={item}
                            item={system_row}
                            initialCurrent={initialCurrent}
                        />
                    )}
                </CodiContext.Consumer>
            </ConfigProvider>
        )}
    </UseHook>
)

export default RowSettingContext
